import {FragmentData, ReviewSectionData} from "src/app/model/ReviewData";
import {GetReviewResponseData} from "src/app/api/model/response/GetReviewResponse";
import {ReOpenFor, ReviewResult, ReviewStatus, ReviewType, ValueType} from "src/app/constant/Enums";

// mock case result fragment
const MOCK_CASE_RESULT_MAIN_FRAGMENT: FragmentData = {
    visible: true,
    rows: [
        {
            context: "BWND Threshold Met: %val",
            elements: [
                {
                    elementType: "PLAIN_TEXT",
                    element: {
                        jsonVal: "A",
                        type: "SINGLE_ITEM"
                    }
                }
            ]
        },
        {
            context: "Lookback Period: %val",
            elements: [
                {
                    element: {
                        jsonVal: "30 days",
                        type: "SINGLE_ITEM"
                    },
                    elementType: "PLAIN_TEXT"
                }
            ]
        },
        {
            context: "AMZL BWNDS: %val ",
            elements: [
                {
                    element: {
                        jsonVal: "10",
                        type: "SINGLE_ITEM"
                    },
                    elementType: "PLAIN_TEXT"
                }
            ]
        },
        {
            context: "SSD BWNDS: %val",
            elements: [
                {
                    element: {
                        jsonVal: "10",
                        type: "SINGLE_ITEM"
                    },
                    elementType: "PLAIN_TEXT"
                }
            ]
        },
        {
            context: "GSF BWNDS: %val",
            elements: [
                {
                    element: {
                        jsonVal: "10",
                        type: "SINGLE_ITEM"
                    },
                    elementType: "PLAIN_TEXT"
                }
            ]
        },
        {
            context: "ROAR BWNDS: %val",
            elements: [
                {
                    element: {
                        jsonVal: "10",
                        type: "SINGLE_ITEM"
                    },
                    elementType: "PLAIN_TEXT"
                }
            ]
        },
        {
            context: "RSR BWNDS: %val",
            elements: [
                {
                    element: {
                        jsonVal: "10",
                        type: "SINGLE_ITEM"
                    },
                    elementType: "PLAIN_TEXT"
                }
            ]
        },
        {
            context: "BWNDs in %val lookback: %val",
            elements: [
                {
                    element: {
                        jsonVal: "30 days",
                        type: "SINGLE_ITEM"
                    },
                    elementType: "PLAIN_TEXT"
                },
                {
                    element: {
                        jsonVal: "50",
                        type: "SINGLE_ITEM"
                    },
                    elementType: "PLAIN_TEXT"
                }
            ]
        },
        {
            context: "Blocks in %val lookback: (%val)",
            elements: [
                {
                    element: {
                        jsonVal: "30 days",
                        type: "SINGLE_ITEM"
                    },
                    elementType: "PLAIN_TEXT"
                },
                {
                    element: {
                        jsonVal: "50",
                        type: "SINGLE_ITEM"
                    },
                    elementType: "PLAIN_TEXT"
                }
            ]
        },
        {
            context: "BWND Rate in %val lookback: %val",
            elements: [
                {
                    element: {
                        jsonVal: "30 days",
                        type: "SINGLE_ITEM"
                    },
                    elementType: "PLAIN_TEXT"
                },
                {
                    element: {
                        jsonVal: "100%",
                        type: "SINGLE_ITEM"
                    },
                    elementType: "PLAIN_TEXT"
                }
            ]
        },
        {
            context: "BWND Probability Score: %val",
            elements: [
                {
                    element: {
                        jsonVal: "0.0125",
                        type: "SINGLE_ITEM"
                    },
                    elementType: "PLAIN_TEXT"
                }
            ]
        },
        {
            context: "AE DP Blurb Sent: %val",
            elements: [
                {
                    elementType: "HREF",
                    element: {
                        jsonVal: "{\"name\":\"blur_link\",\"href\":\"https://quip-amazon.com/MqWvAXIm9LOP/Eagle-Eye-Portal-LLD\"}",
                        type: "MAP"
                    }
                }
            ]
        },
        {
            context: "%val",
            elements: [
                {
                    elementType: "PLAIN_TEXT",
                    element: {
                        jsonVal: "[\"CASE_DETAIL_1\",\"CASE_DATA_POINT_1\"]",
                        type: "LIST"
                    }
                }
            ]
        }
    ]
}

const MOCK_CASE_RESULT_FRAGMENTS = new Map<string, FragmentData>();

MOCK_CASE_RESULT_FRAGMENTS.set("main", MOCK_CASE_RESULT_MAIN_FRAGMENT);

const MOCK_CASE_RESULT_REVIEW_SECTION_DATA: ReviewSectionData = {
    fragments: MOCK_CASE_RESULT_FRAGMENTS,
    title: "Case Data Point"
}

// mock image review fragment
const MOCK_REVIEW_SECTION_IMAGE_FRAGMENT: FragmentData = {
    visible: true,
    rows: [
        {
            context: "%val",
            elements: [
                {
                    elementType: "IMAGE",
                    element: {
                        jsonVal: "https://miro.medium.com/v2/resize:fit:1200/1*20t6cUfMUcxWn8GFb94sPg.png",
                        type: "SINGLE_ITEM"
                    }
                }
            ]
        }
    ]
}

const MOCK_IMAGE_REVIEW_FRAGMENTS = new Map<string, FragmentData>();

MOCK_IMAGE_REVIEW_FRAGMENTS.set("main", MOCK_REVIEW_SECTION_IMAGE_FRAGMENT);

const MOCK_IMAGE_REVIEW_SECTION_DATA: ReviewSectionData = {
    fragments: MOCK_IMAGE_REVIEW_FRAGMENTS,
    title: "DL Review"
}

// mock user input fragment
const MOCK_REVIEW_SECTION_USER_INPUT_FRAGMENT: FragmentData = {
    visible: true,
    rows: [
        {
            context: "Was the device used by DP mapped to at least 5 other DP ids? %val",
            elements: [
                {
                    elementType: "RADIO_BUTTON_GROUP",
                    element: {
                        jsonVal: "{\"options\":[\"No\",\"Yes\",\"N/A\"],\"selected\":\"No\",\"key\":\"auditorInput-answer-1\"}",
                        type: "MAP"
                    }
                }
            ]
        },
        {
            context: "Was the BWND rate of the DP matching our threshold criteria (>0.3)? %val",
            elements: [
                {
                    elementType: "RADIO_BUTTON_GROUP",
                    element: {
                        jsonVal: "{\"options\":[\"No\",\"Yes\",\"N/A\"],\"selected\":\"Yes\",\"key\":\"auditorInput-answer-2\"}",
                        type: "MAP"
                    }
                }
            ]
        },
        {
            context: "Was the Defect rate of the DP matching our threshold criteria (>0.2)? %val",
            elements: [
                {
                    elementType: "RADIO_BUTTON_GROUP",
                    element: {
                        jsonVal: "{\"options\":[\"No\",\"Yes\",\"N/A\"],\"selected\":\"Yes\",\"key\":\"auditorInput-answer-3\"}",
                        type: "MAP"
                    }
                }
            ]
        },
        {
            context: "Was there any selfie taken by the DP in the last 30 days ? %val",
            elements: [
                {
                    elementType: "RADIO_BUTTON_GROUP",
                    element: {
                        jsonVal: "{\"options\":[\"No\",\"Yes\",\"N/A\"],\"selected\":\"No\",\"key\":\"auditorInput-answer-4\"}",
                        type: "MAP"
                    }
                }
            ]
        },    
    ]
}

const MOCK_USER_INPUT_REVIEW_FRAGMENTS = new Map<string, FragmentData>();

MOCK_USER_INPUT_REVIEW_FRAGMENTS.set("main", MOCK_REVIEW_SECTION_USER_INPUT_FRAGMENT);

const MOCK_USER_INPUT_REVIEW_SECTION_DATA: ReviewSectionData = {
    fragments: MOCK_USER_INPUT_REVIEW_FRAGMENTS,
    title: "Audit Checks"
}

const MOCK_LAST_REVIEW_DECISION_DETAIL = {
    reasonCode: {
        jsonVal: "Approval",
        type: ValueType.SINGLE_ITEM,
    },
    comment: {
        jsonVal: "comment",
        type: ValueType.SINGLE_ITEM,
    }
}

export const MOCK_REVIEW_DATA: GetReviewResponseData = {
    caseType: "BWND",
    caseSubType: "BWND",
    businessType: "DEFAULT",
    lastReviewDecisionDetail: MOCK_LAST_REVIEW_DECISION_DETAIL,
    country: "US",
    creationDate: "2024-05-17T15:11:07.057569896Z",
    lastModifiedBy: "ewm",
    lastModifiedDate: "2024-05-17T15:11:07.057630113Z",
    referenceId: "A1GQ9DN6E5FYYO",
    referenceSubType: "DP",
    referenceType: "Transporter",
    reviewId: "0afa6528-95ca-4ba0-9fd4-c56132b8e3eb",
    reviewStatus: ReviewStatus.PENDING,
    reviewResult: ReviewResult.NO_ACTION,
    uiLayoutId: "DEFAULT_UI_LAYOUT",
    reviewType: ReviewType.PRIMARY_AUDIT,
    reOpenFor: 'RECTIFYING',
    reviewSections: [MOCK_CASE_RESULT_REVIEW_SECTION_DATA, MOCK_USER_INPUT_REVIEW_SECTION_DATA,
        MOCK_CASE_RESULT_REVIEW_SECTION_DATA, MOCK_IMAGE_REVIEW_SECTION_DATA]
}

//-------------------------------------------ODQ/RELIABILITY Mock Data-----------------------------------------------//
const MOCK_CASE_HISTORY_ENTRIES = Array.from({length: 30}, (_, i) => {
    const date = new Date(2026, 7, 28 - i);
    const hours = 8 + (i % 10);
    const actions = ["No action", "Offboard", "Reopen", "No action", "Offboard"];
    const types = ["PRIMARY_AUDIT", "SECONDARY_AUDIT", "PRIMARY_AUDIT", "PRIMARY_AUDIT", "SECONDARY_AUDIT"];
    const duration = 3 + (i % 6);
    const timestamp = `${date.toISOString().split('T')[0]} `
        + `${hours}:${String((i * 7) % 60).padStart(2, '0')}:00 UTC`;
    return `<${timestamp}> [auditor_${i + 1}] took "${actions[i % 5]}" decision`
        + ` for DP as part of [${types[i % 5]}] (duration [${duration}] seconds)`;
});

const MOCK_CASE_HISTORY_FRAGMENT: FragmentData = {
    visible: true,
    rows: [
        {
            context: "%val",
            elements: [
                {
                    elementType: "PLAIN_TEXT",
                    element: {
                        jsonVal: JSON.stringify(MOCK_CASE_HISTORY_ENTRIES),
                        type: "LIST"
                    }
                }
            ]
        }
    ]
}

const MOCK_CASE_HISTORY_FRAGMENTS = new Map<string, FragmentData>();
MOCK_CASE_HISTORY_FRAGMENTS.set("main", MOCK_CASE_HISTORY_FRAGMENT);

const MOCK_CASE_HISTORY_SECTION: ReviewSectionData = {
    fragments: MOCK_CASE_HISTORY_FRAGMENTS,
    title: "Case History"
}

// ODQ Case Details section (config-driven)
const ODQ_CASE_DETAILS_FRAGMENT: FragmentData = {
    visible: true,
    rows: [
        {
            context: "Transporter ID: %val (%val)",
            elements: [
                {
                    elementType: "HREF",
                    element: {
                        jsonVal: "{\"name\":\"ANYX1928XCVNE\",\"href\":\"https://logistics.amazon.com/amconsole/transporter/ANYX1928XCVNE\",\"target\":\"_blank\"}",
                        type: "MAP"
                    }
                },
                {
                    elementType: "PLAIN_TEXT",
                    element: {
                        jsonVal: "ACTIVE",
                        type: "SINGLE_ITEM"
                    }
                }
            ]
        },
        {
            context: "Country: %val",
            elements: [
                {
                    elementType: "PLAIN_TEXT",
                    element: {
                        jsonVal: "US",
                        type: "SINGLE_ITEM"
                    }
                }
            ]
        }
    ]
}

const ODQ_CASE_DETAILS_FRAGMENTS = new Map<string, FragmentData>();
ODQ_CASE_DETAILS_FRAGMENTS.set("main", ODQ_CASE_DETAILS_FRAGMENT);

const ODQ_CASE_DETAILS_SECTION: ReviewSectionData = {
    fragments: ODQ_CASE_DETAILS_FRAGMENTS,
    title: "Case Details"
}

// ODQ Audit Checks section (config-driven, plain text questions)
const ODQ_AUDIT_CHECKS_FRAGMENT: FragmentData = {
    visible: true,
    rows: [
        {
            context: "%val",
            elements: [
                {
                    elementType: "PLAIN_TEXT",
                    element: {
                        jsonVal: JSON.stringify([
                            "1) Is Driver over the package cap (Y/N):",
                            "2) Any relevant LSE tickets found (Y/N):",
                            "3) Any relevant station delays found (Y/N):",
                            "4) a) Planned delivery outside of business hours (Y/N):",
                            "\u00A0\u00A0\u00A0b) Any Secure Location scans found (Y/N):",
                            "\u00A0\u00A0\u00A0c) Any Customer unavailable instance (Y/N):",
                            "5) Any Access/Locker Issues found (Y/N):",
                            "6) Any valid LHE conditions(Y/N):",
                            "7)  Any older or other defect Exempted :",
                            "8 ) a) DNR Defects adjudicated (Y/N):",
                            "\u00A0\u00A0\u00A0b) Initial number of DNR defects :",
                            "\u00A0\u00A0\u00A0c) Total defects after removing Amazon and customer responsible DNRs :",
                            "\u00A0\u00A0\u00A0d) Exemptions granted, if any? (Y/N):",
                            "\u00A0\u00A0\u00A0e) Reasons for exemptions :",
                            "Sanity Checks:",
                            "1) Are there applicable SYSKA's on DPs account? (Y/N):",
                            "2) Is the DP listed on the DND List?(Y/N):",
                            "3) Does the DP have an active High-Severity ticket? (Y/N):"
                        ]),
                        type: "LIST"
                    }
                }
            ]
        }
    ]
}

const ODQ_AUDIT_CHECKS_FRAGMENTS = new Map<string, FragmentData>();
ODQ_AUDIT_CHECKS_FRAGMENTS.set("main", ODQ_AUDIT_CHECKS_FRAGMENT);

const ODQ_AUDIT_CHECKS_SECTION: ReviewSectionData = {
    fragments: ODQ_AUDIT_CHECKS_FRAGMENTS,
    title: "Audit Checks"
}


// ODQ Mock Defects
export const MOCK_ODQ_DEFECTS = [
    {opportunityId: "tr-cw-amzl-3ccfa140-fc72-58a1-3b41-3da54d107a5b", trackingId: "TBA332650410896", defectType: "LATE_DELIVERY_PACKAGE", defectDate: "2026-07-08T23:15:00Z", orderStartTime: "2026-07-08T22:00:00Z", orderEndTime: "2026-07-09T01:00:00Z", stationCode: "DSW3", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-04cfba38-907e-5ff9-9c82-e34ffb0ae273", trackingId: "TBA332920583916", defectType: "UNRETURNED_PACKAGE", defectDate: "2026-07-18T10:15:00Z", orderStartTime: "2026-07-18T10:00:00Z", orderEndTime: "2026-07-18T13:00:00Z", stationCode: "VWA1", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-1ccfd9b3-fc06-f2b4-1a2b-28430a606460", trackingId: "TBA333263515900", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-30T15:00:00Z", orderStartTime: "2026-07-30T14:00:00Z", orderEndTime: "2026-07-30T17:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-22cfd9b7-0f20-e081-a6b5-93f2e0371494", trackingId: "TBA333263661729", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-30T15:00:00Z", orderStartTime: "2026-07-30T16:00:00Z", orderEndTime: "2026-07-30T19:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-24cfd7f6-bec3-8df3-0a48-78c4d81dbe94", trackingId: "TBA333244622932", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-30T15:00:00Z", orderStartTime: "2026-07-30T16:00:00Z", orderEndTime: "2026-07-30T19:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-26cfd9b2-b7cd-c6a4-92f8-49a191a8bcce", trackingId: "TBA333263453407", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-30T15:00:00Z", orderStartTime: "2026-07-30T14:00:00Z", orderEndTime: "2026-07-30T17:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-2ecfc141-e090-b75f-283e-b7ea0b36d89e", trackingId: "TBA332994909492", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-21T04:00:00Z", orderStartTime: "2026-07-21T03:00:00Z", orderEndTime: "2026-07-21T06:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-2ecfc141-e090-b75f-283e-b7ea0b36d89e", trackingId: "TBA332994909492", defectType: "UNRETURNED_PACKAGE", defectDate: "2026-07-21T04:00:00Z", orderStartTime: "2026-07-21T03:00:00Z", orderEndTime: "2026-07-21T06:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-44cfd8ab-9c06-c9b4-248d-1536c6a84cf3", trackingId: "TBA333254325340", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-30T08:00:00Z", orderStartTime: "2026-07-30T07:00:00Z", orderEndTime: "2026-07-30T11:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-44cfd8ab-9c06-c9b4-248d-1536c6a84cf3", trackingId: "TBA333254325340", defectType: "UNRETURNED_PACKAGE", defectDate: "2026-07-30T08:00:00Z", orderStartTime: "2026-07-30T07:00:00Z", orderEndTime: "2026-07-30T11:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-4acfcfa0-6024-f98a-ce2a-dbfd1a96d804", trackingId: "TBA333149825480", defectType: "LATE_DELIVERY_PACKAGE", defectDate: "2026-07-26T17:00:00Z", orderStartTime: "2026-07-26T16:00:00Z", orderEndTime: "2026-07-26T19:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-50cfc358-ee9f-e783-c1d7-e6afdfbdd32c", trackingId: "TBA333018373426", defectType: "LATE_DELIVERY_PACKAGE", defectDate: "2026-07-22T15:00:00Z", orderStartTime: "2026-07-22T16:00:00Z", orderEndTime: "2026-07-22T19:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-52cfc02c-810e-4a53-9f68-5769f27a0316", trackingId: "TBA332980059978", defectType: "LATE_DELIVERY_PACKAGE", defectDate: "2026-07-21T04:00:00Z", orderStartTime: "2026-07-21T03:00:00Z", orderEndTime: "2026-07-21T06:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-6acfb6a4-d63b-806f-2ccd-638e166fc7db", trackingId: "TBA332882847501", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-17T13:45:00Z", orderStartTime: "2026-07-17T14:00:00Z", orderEndTime: "2026-07-17T17:00:00Z", stationCode: "VWA1", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-6ecfb6e4-09b4-8cd9-0542-b2acb2fcea27", trackingId: "TBA332886071873", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-17T13:45:00Z", orderStartTime: "2026-07-17T14:00:00Z", orderEndTime: "2026-07-17T17:00:00Z", stationCode: "VWA1", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-86cfd0aa-0fcb-b87e-b8b8-65ed3e75f792", trackingId: "TBA333162754043", defectType: "LATE_DELIVERY_PACKAGE", defectDate: "2026-07-27T02:15:00Z", orderStartTime: "2026-07-27T02:00:52Z", orderEndTime: "2026-07-27T05:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-94cfcfa4-2713-9925-8aca-7e8579b75c4e", trackingId: "TBA333149989751", defectType: "LATE_DELIVERY_PACKAGE", defectDate: "2026-07-26T17:00:00Z", orderStartTime: "2026-07-26T16:00:00Z", orderEndTime: "2026-07-26T19:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-a0cfd9b4-8125-a52c-0948-fcd5d782326e", trackingId: "TBA333263541375", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-30T15:00:00Z", orderStartTime: "2026-07-30T14:00:00Z", orderEndTime: "2026-07-30T17:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-aacfd756-c1d6-2751-2f0a-ff096d84a7ce", trackingId: "TBA333236746763", defectType: "LATE_DELIVERY_PACKAGE", defectDate: "2026-07-30T08:00:00Z", orderStartTime: "2026-07-30T07:00:00Z", orderEndTime: "2026-07-30T11:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-b4cfba50-a174-2534-253c-44d4019148cb", trackingId: "TBA332921058366", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-18T10:15:00Z", orderStartTime: "2026-07-18T10:00:00Z", orderEndTime: "2026-07-18T13:00:00Z", stationCode: "VWA1", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-b4cfba50-a174-2534-253c-44d4019148cb", trackingId: "TBA332921058366", defectType: "UNRETURNED_PACKAGE", defectDate: "2026-07-18T10:15:00Z", orderStartTime: "2026-07-18T10:00:00Z", orderEndTime: "2026-07-18T13:00:00Z", stationCode: "VWA1", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-bccfd8c3-da32-ecd6-ae8a-e1db9698b76f", trackingId: "TBA333255733738", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-30T08:00:00Z", orderStartTime: "2026-07-30T07:00:00Z", orderEndTime: "2026-07-30T11:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-bccfd8c3-da32-ecd6-ae8a-e1db9698b76f", trackingId: "TBA333255733738", defectType: "UNRETURNED_PACKAGE", defectDate: "2026-07-30T08:00:00Z", orderStartTime: "2026-07-30T07:00:00Z", orderEndTime: "2026-07-30T11:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-c0cfd029-fdd9-80a1-39bf-e678ea8ad5e0", trackingId: "TBA333156535652", defectType: "LATE_DELIVERY_PACKAGE", defectDate: "2026-07-26T21:45:00Z", orderStartTime: "2026-07-26T21:19:12Z", orderEndTime: "2026-07-27T00:10:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-c8cfb819-149a-14d3-5416-c2e674430673", trackingId: "TBA332888338412", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-17T13:45:00Z", orderStartTime: "2026-07-17T14:00:00Z", orderEndTime: "2026-07-17T17:00:00Z", stationCode: "VWA1", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-cccfb2c0-bba3-3d65-3679-9d52cc1c8b7e", trackingId: "TBA332839922538", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-15T11:45:00Z", orderStartTime: "2026-07-15T12:00:00Z", orderEndTime: "2026-07-15T15:00:00Z", stationCode: "VWA1", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-cecfb910-114c-2945-3876-fc18c1d49c04", trackingId: "TBA332907780183", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-18T10:15:00Z", orderStartTime: "2026-07-18T10:00:00Z", orderEndTime: "2026-07-18T13:00:00Z", stationCode: "VWA1", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-cecfb910-114c-2945-3876-fc18c1d49c04", trackingId: "TBA332907780183", defectType: "UNRETURNED_PACKAGE", defectDate: "2026-07-18T10:15:00Z", orderStartTime: "2026-07-18T10:00:00Z", orderEndTime: "2026-07-18T13:00:00Z", stationCode: "VWA1", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-d2cfd9b6-c718-d944-54fd-afa54fc20bae", trackingId: "TBA333263646861", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-30T15:00:00Z", orderStartTime: "2026-07-30T14:00:00Z", orderEndTime: "2026-07-30T17:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-d4cfd9b4-a397-5690-596d-0090d25e684b", trackingId: "TBA333263548087", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-30T15:00:00Z", orderStartTime: "2026-07-30T14:00:00Z", orderEndTime: "2026-07-30T17:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-d8cfcfa0-11ba-2cea-fd56-3621e44ec9d2", trackingId: "TBA333149811397", defectType: "LATE_DELIVERY_PACKAGE", defectDate: "2026-07-26T17:00:00Z", orderStartTime: "2026-07-26T16:00:00Z", orderEndTime: "2026-07-26T19:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-e0cfb811-5a59-c1a1-03b5-0670daa73404", trackingId: "TBA332896735700", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-17T13:45:00Z", orderStartTime: "2026-07-17T14:00:00Z", orderEndTime: "2026-07-17T17:00:00Z", stationCode: "VWA1", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-e2cfd9b6-39e0-2a3f-a0ac-49cb4a40b9cf", trackingId: "TBA333263621278", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-30T15:00:00Z", orderStartTime: "2026-07-30T20:00:00Z", orderEndTime: "2026-07-30T23:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-e8cfc517-0288-db5c-0feb-c1a67f8979b7", trackingId: "TBA333036757726", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-22T15:00:00Z", orderStartTime: "2026-07-22T14:00:00Z", orderEndTime: "2026-07-22T17:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-f2cfd9b3-6126-79d6-5ec4-e1ae4887e9de", trackingId: "TBA333263478938", defectType: "UNDELIVERED_PACKAGE", defectDate: "2026-07-30T15:00:00Z", orderStartTime: "2026-07-30T16:00:00Z", orderEndTime: "2026-07-30T19:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-no-tracking-001", trackingId: "N/A", defectType: "LATE_DELIVERY_PACKAGE", defectDate: "2026-07-25T12:00:00Z", orderStartTime: "2026-07-25T11:00:00Z", orderEndTime: "2026-07-25T14:00:00Z", stationCode: "DAL2", isExempted: false},
    {opportunityId: "tr-cw-ssdnal-no-tracking-002", trackingId: "", defectType: "UNRETURNED_PACKAGE", defectDate: "2026-07-28T09:00:00Z", orderStartTime: "2026-07-28T08:00:00Z", orderEndTime: "2026-07-28T11:00:00Z", stationCode: "VWA4", isExempted: false},
    {opportunityId: "tr-cw-amzl-e0cffc18-173b-11b3-709f-b65ae0c0f154", trackingId: "TBA333654639809", defectType: "UNATTENDED_DNR", defectDate: "2026-07-21T04:30:00Z", orderStartTime: "2026-07-21T03:00:00Z", orderEndTime: "2026-07-21T06:00:00Z", stationCode: "DOT4", concessionTime: "2026-07-22T00:27:40Z", isExempted: false},
    {opportunityId: "tr-cw-amzl-1ed01cc0-416c-7943-e719-be910b2458d1", trackingId: "TBA334033218278", defectType: "UNATTENDED_DNR", defectDate: "2026-07-30T08:45:00Z", orderStartTime: "2026-07-30T07:00:00Z", orderEndTime: "2026-07-30T11:00:00Z", stationCode: "DOT4", concessionTime: "2026-07-31T00:45:00Z", isExempted: false},
    {opportunityId: "tr-cw-amzl-a3d02010-5523-8a01-c3e1-d4f21098bc45", trackingId: "TBA334100567123", defectType: "UNATTENDED_DNR", defectDate: "2026-07-15T14:30:00Z", orderStartTime: "2026-07-15T12:00:00Z", orderEndTime: "2026-07-15T16:00:00Z", stationCode: "DOT4", concessionTime: "2026-07-16T14:30:00Z", isExempted: false},
];

// Mock Reason Codes (simulates GET /odq/reason-codes response)
export const MOCK_REASON_CODES = [
    {
        category: {categoryKey: "UNSAFE", title: "Unsafe Conditions"},
        reasonCodes: [
            {reasonCodeKey: "LSPE_UNSAFE_SNOW", title: "LSPE Unsafe Snow"},
            {reasonCodeKey: "LSPE_UNSAFE_COLLISION", title: "LSPE Unsafe Collision"},
            {reasonCodeKey: "LSPE_UNSAFE_THREAT", title: "LSPE Unsafe Threat"},
        ]
    },
    {
        category: {categoryKey: "LSPE_LHE", title: "LHE/LSPE"},
        reasonCodes: [
            {reasonCodeKey: "LSPE_APP_CRASH", title: "LSPE App Crash"},
            {reasonCodeKey: "LSPE_LHE_DEVICE_ACCESS", title: "LSPE LHE Device Access"},
            {reasonCodeKey: "LSPE_LHE_VEHICLE_ISSUE", title: "LSPE LHE Vehicle Issue"},
            {reasonCodeKey: "LSPE_APP_ISSUE_ITINERARY_ROUTING", title: "LSPE App Issue Itinerary Routing"},
        ]
    },
    {
        category: {categoryKey: "NO_SHOW_LATE_CANCEL", title: "No Show/Late Cancel"},
        reasonCodes: [
            {reasonCodeKey: "OLD_DEFECT_FOUND", title: "Old defect found"},
            {reasonCodeKey: "ALERT_NOT_SENT", title: "Alert Not Sent"},
        ]
    },
    {
        category: {categoryKey: "RETURN_TO_STATION", title: "Return to Station"},
        reasonCodes: [
            {reasonCodeKey: "RTS_US_GSF_ALCOHOL", title: "RTS US GSF Alcohol"},
            {reasonCodeKey: "RTS_TECHNICAL_ISSUES", title: "Technical Issues"},
        ]
    },
];

// Main ODQ mock response
export const MOCK_ODQ_REVIEW_DATA: GetReviewResponseData = {
    caseType: "Abuse",
    caseSubType: "ODQ",
    businessType: "DEFAULT",
    lastReviewDecisionDetail: MOCK_LAST_REVIEW_DECISION_DETAIL,
    country: "US",
    creationDate: "2026-07-15T10:00:00.000Z",
    lastModifiedBy: "dummy-user",
    lastModifiedDate: "2026-08-05T14:30:00.000Z",
    referenceId: "ANYX1928XCVNE",
    referenceSubType: "DP",
    referenceType: "Transporter",
    reviewId: "odq-test-review-001",
    reviewStatus: ReviewStatus.PENDING,
    reviewResult: ReviewResult.PENDING,
    uiLayoutId: "ODQ_AND_RELIABILITY_CUSTOM_LAYOUT",
    reviewType: ReviewType.PRIMARY_AUDIT,
    reviewSections: [ODQ_CASE_DETAILS_SECTION, ODQ_AUDIT_CHECKS_SECTION, MOCK_CASE_HISTORY_SECTION]
}

// RELIABILITY mock defects
export const MOCK_RELIABILITY_DEFECTS = [
    {opportunityId: "amzn1.flex.pr.v1.A1H28CZQSXI2J1.1781001900000.fd7e2d10-2c7b-427d-bf05-c6dcca7551d4.amzn1.flex.st.v1.PuyOplzlR1idvfPkv5138g.CSP.EMPTY.210", defectType: "NO_SHOW", defectDate: "2026-06-09T10:45:00Z", blockStartTime: "2026-06-09T10:45:00Z", blockEndTime: "2026-06-09T14:15:00Z", isExempted: false},
    {opportunityId: "amzn1.flex.pr.v1.A1H28CZQSXI2J1.1780656300000.fd7e2d10-2c7b-427d-bf05-c6dcca7551d4.amzn1.flex.st.v1.PuyOplzlR1idvfPkv5138g.CSP.EMPTY.210", defectType: "NO_SHOW", defectDate: "2026-06-05T10:45:00Z", blockStartTime: "2026-06-05T10:45:00Z", blockEndTime: "2026-06-05T14:15:00Z", isExempted: false},
    {opportunityId: "amzn1.flex.pr.v1.A1H28CZQSXI2J1.1779099300000.fd7e2d10-2c7b-427d-bf05-c6dcca7551d4.amzn1.flex.st.v1.PuyOplzlR1idvfPkv5138g.CSP.EMPTY.210", defectType: "NO_SHOW", defectDate: "2026-05-18T10:15:00Z", blockStartTime: "2026-05-18T10:15:00Z", blockEndTime: "2026-05-18T13:45:00Z", isExempted: false},
    {opportunityId: "amzn1.flex.pr.v1.A1H28CZQSXI2J1.1760374800000.fd7e2d10-2c7b-427d-bf05-c6dcca7551d4.amzn1.flex.st.v1.PuyOplzlR1idvfPkv5138g.CSP.EMPTY.210", defectType: "LATE_CANCEL", defectDate: "2025-10-13T17:00:00Z", blockStartTime: "2025-10-13T17:00:00Z", blockEndTime: "2025-10-13T20:30:00Z", isExempted: false},
    {opportunityId: "amzn1.flex.pr.v1.A1H28CZQSXI2J1.1759833900000.fd7e2d10-2c7b-427d-bf05-c6dcca7551d4.amzn1.flex.st.v1.PuyOplzlR1idvfPkv5138g.CSP.EMPTY.210", defectType: "NO_SHOW", defectDate: "2025-10-07T10:45:00Z", blockStartTime: "2025-10-07T10:45:00Z", blockEndTime: "2025-10-07T14:15:00Z", isExempted: false},
    {opportunityId: "amzn1.flex.pr.v1.A1H28CZQSXI2J1.1746873000000.fd7e2d10-2c7b-427d-bf05-c6dcca7551d4.amzn1.flex.st.v1.PuyOplzlR1idvfPkv5138g.CSP.EMPTY.210", defectType: "NO_SHOW", defectDate: "2025-05-10T10:30:00Z", blockStartTime: "2025-05-10T10:30:00Z", blockEndTime: "2025-05-10T14:00:00Z", isExempted: false},
    {opportunityId: "amzn1.flex.pr.v1.A1H28CZQSXI2J1.1746826200000.fd7e2d10-2c7b-427d-bf05-c6dcca7551d4.amzn1.flex.st.v1.PuyOplzlR1idvfPkv5138g.CSP.EMPTY.210", defectType: "NO_SHOW", defectDate: "2025-05-09T21:30:00Z", blockStartTime: "2025-05-09T21:30:00Z", blockEndTime: "2025-05-10T01:00:00Z", isExempted: false},
    {opportunityId: "amzn1.flex.pr.v1.no-block-rel-001", defectType: "NO_SHOW", defectDate: "2026-07-15T08:00:00Z", blockStartTime: "", blockEndTime: "", isExempted: false},
    {opportunityId: "amzn1.flex.pr.v1.no-block-rel-002", defectType: "LATE_CANCEL", defectDate: "2025-07-10T12:00:00Z", blockStartTime: "", blockEndTime: "", isExempted: false},
];

// RELIABILITY mock response
export const MOCK_RELIABILITY_REVIEW_DATA: GetReviewResponseData = {
    caseType: "Abuse",
    caseSubType: "RELIABILITY",
    businessType: "DEFAULT",
    lastReviewDecisionDetail: MOCK_LAST_REVIEW_DECISION_DETAIL,
    country: "US",
    creationDate: "2026-07-15T10:00:00.000Z",
    lastModifiedBy: "dummy-user",
    lastModifiedDate: "2026-08-05T14:30:00.000Z",
    referenceId: "ANYX1928XCVNE",
    referenceSubType: "DP",
    referenceType: "Transporter",
    reviewId: "reliability-test-001",
    reviewStatus: ReviewStatus.PENDING,
    reviewResult: ReviewResult.PENDING,
    uiLayoutId: "ODQ_AND_RELIABILITY_CUSTOM_LAYOUT",
    reviewType: ReviewType.PRIMARY_AUDIT,
    reviewSections: [ODQ_CASE_DETAILS_SECTION, ODQ_AUDIT_CHECKS_SECTION, MOCK_CASE_HISTORY_SECTION]
}

// Mock Standing Responses (simulates GET /odq/providers/{providerId}/standing?opportunityType=X)
export const MOCK_ODQ_STANDING_RESPONSE = {
    result: 'SUCCESS',
    msg: 'OK',
    data: {
        overallStanding: 'FAIR',
        standingBucketPosition: 3,
        scores: [
            {scoreType: 'ON_TIME_DELIVERIES', value: 0.97, issueRecency: 'NO_RECENT_ISSUES', standingBucket: 'FANTASTIC'},
            {scoreType: 'DELIVERED_NOT_RECEIVED', value: 0.99, issueRecency: 'NO_ISSUES', standingBucket: 'FANTASTIC'},
            {scoreType: 'DELIVERY_COMPLETION_RATE', value: 0.88, issueRecency: 'HAS_RECENT_ISSUES', standingBucket: 'FAIR'},
            {scoreType: 'UNRETURNED', value: 0.95, issueRecency: 'NO_RECENT_ISSUES', standingBucket: 'FANTASTIC'},
        ],
        hasActiveOffboarding: false,
        dataFreshnessDate: '2026-08-08T00:00:00Z',
        // Deliberately an earlier, different date from freshness: the two are distinct concepts and
        // a crossed wire would be invisible if they matched.
        createdDate: '2026-08-01T09:30:00Z',
        panelStatus: 'OK' as const,
    }
};

export const MOCK_RELIABILITY_STANDING_RESPONSE = {
    result: 'SUCCESS',
    msg: 'OK',
    data: {
        overallStanding: 'FAIR',
        standingBucketPosition: 3,
        scores: [
            {scoreType: 'RELIABILITY', value: 0.95, issueRecency: 'NO_RECENT_ISSUES', standingBucket: 'GREAT'},
            {scoreType: 'NO_SHOW', value: 0.98, issueRecency: 'NO_ISSUES', standingBucket: 'FANTASTIC'},
            {scoreType: 'LATE_CANCEL', value: 0.96, issueRecency: 'NO_RECENT_ISSUES', standingBucket: 'FANTASTIC'},
        ],
        hasActiveOffboarding: false,
        dataFreshnessDate: '2026-08-08T00:00:00Z',
        // Deliberately an earlier, different date from freshness: the two are distinct concepts and
        // a crossed wire would be invisible if they matched.
        createdDate: '2026-08-01T09:30:00Z',
        panelStatus: 'OK' as const,
    }
};

// Backward-compatible alias for existing tests
export const MOCK_STANDING_RESPONSE = MOCK_ODQ_STANDING_RESPONSE;

// Mock SLP Ticket Response (simulates POST /odq/cases/{caseId}/createtickettoslp)
export const MOCK_SLP_TICKET_RESPONSE = {
    result: 'SUCCESS',
    msg: 'OK',
    data: {
        status: 'CREATED' as const,
        ticketId: 'V2247773670',
        ticketLink: 'https://t.corp.amazon.com/V2247773670/overview',
    }
};
